export const hasTextContent = (html: string | undefined): boolean => {
  if (!html) {
    return false;
  }

  const strippedText = html.replace(/<[^>]*>/g, '').trim();

  return strippedText.length > 0;
};

/**
 * Calculates the duration between a start date and now, returning years and months
 * @param hostingSince - ISO date string representing when hosting started
 * @returns Object with years and months, or null if date is invalid
 */
export const calculateHostingDuration = (
  hostingSince: string | undefined
): { years: number; months: number } | null => {
  if (!hostingSince) {
    return null;
  }

  const startDate = new Date(hostingSince);

  if (isNaN(startDate.getTime())) {
    return null;
  }

  const now = new Date();
  let years = now.getFullYear() - startDate.getFullYear();
  let months = now.getMonth() - startDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  // If the day of month in startDate is later than today, subtract one month
  if (now.getDate() < startDate.getDate()) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
  }

  return { years, months };
};

/**
 * Formats hosting duration as a human-readable string
 * Shows only months if less than 12 months, only years if 12 months or more
 * @param duration - Object with years and months
 * @returns Formatted string like "2 Years" or "5 Months"
 */
export const formatHostingDuration = (
  duration: { years: number; months: number } | null
): string | null => {
  if (!duration) {
    return null;
  }

  const { years, months } = duration;

  if (years === 0 && months === 0) {
    return 'Less than 1 month';
  }

  // If we have at least 1 year, show only years (ignore months)
  if (years > 0) {
    return `${years} ${years === 1 ? 'Year' : 'Years'}`;
  }

  // Otherwise show only months
  return `${months} ${months === 1 ? 'Month' : 'Months'}`;
};
